import type { TabConfig, TabsBlockConfig, TabStyleConfig } from '@lighthouse/core'
import { ALIGNMENT, BACKGROUND_TYPE, /* SIZE_FIT, */ TABS_TYPE, VariableType } from '@lighthouse/core'
import { nanoid } from '@lighthouse/tools'

const LINE_COLOR_DEFAULT: Omit<TabStyleConfig, 'id'> = {
    color: { type: BACKGROUND_TYPE.color, color: '#98A2B3' },
    background: { type: BACKGROUND_TYPE.color, color: '' },
    borderColor: { type: BACKGROUND_TYPE.color, color: '' },
    iconColor: { type: BACKGROUND_TYPE.color, color: '#98A2B3' },
    hover: {
        color: { type: BACKGROUND_TYPE.color, color: '#98A2B3' },
        background: { type: BACKGROUND_TYPE.color, color: '#F9FAFB' },
        borderColor: { type: BACKGROUND_TYPE.color, color: '' },
        iconColor: { type: BACKGROUND_TYPE.color, color: '#98A2B3' }
    },
    active: {
        color: { type: BACKGROUND_TYPE.color, color: '#101828' },
        background: { type: BACKGROUND_TYPE.color, color: '' },
        borderColor: { type: BACKGROUND_TYPE.color, color: '' },
        iconColor: { type: BACKGROUND_TYPE.color, color: '#101828' }
    }
}

const CARD_COLOR_DEFAULT: Omit<TabStyleConfig, 'id'> = {
    color: { type: BACKGROUND_TYPE.color, color: '#98A2B3' },
    background: { type: BACKGROUND_TYPE.color, color: '#F9FAFB' },
    borderColor: { type: BACKGROUND_TYPE.color, color: '#e4e7ec' },
    iconColor: { type: BACKGROUND_TYPE.color, color: '#98A2B3' },
    hover: {
        color: { type: BACKGROUND_TYPE.color, color: '#98A2B3' },
        background: { type: BACKGROUND_TYPE.color, color: '#ECEFF4' },
        borderColor: { type: BACKGROUND_TYPE.color, color: '#e4e7ec' },
        iconColor: { type: BACKGROUND_TYPE.color, color: '#98A2B3' }
    },
    active: {
        color: { type: BACKGROUND_TYPE.color, color: '#101828' },
        background: { type: BACKGROUND_TYPE.color, color: '#FFFFFF' },
        borderColor: { type: BACKGROUND_TYPE.color, color: '#e4e7ec' },
        iconColor: { type: BACKGROUND_TYPE.color, color: '#101828' }
    }
}

const ROUND_COLOR_DEFAULT: Omit<TabStyleConfig, 'id'> = {
    color: { type: BACKGROUND_TYPE.color, color: '#98A2B3' },
    background: { type: BACKGROUND_TYPE.color, color: '' },
    borderColor: { type: BACKGROUND_TYPE.color, color: '' },
    iconColor: { type: BACKGROUND_TYPE.color, color: '#98A2B3' },
    hover: {
        color: { type: BACKGROUND_TYPE.color, color: '#98A2B3' },
        background: { type: BACKGROUND_TYPE.color, color: '' },
        borderColor: { type: BACKGROUND_TYPE.color, color: '' },
        iconColor: { type: BACKGROUND_TYPE.color, color: '#98A2B3' }
    },
    active: {
        color: { type: BACKGROUND_TYPE.color, color: '#101828' },
        background: { type: BACKGROUND_TYPE.color, color: '#ECEFF4' },
        borderColor: { type: BACKGROUND_TYPE.color, color: '' },
        iconColor: { type: BACKGROUND_TYPE.color, color: '#101828' }
    }
}

const SEGMENTED_COLOR_DEFAULT: Omit<TabStyleConfig, 'id'> = {
    color: { type: BACKGROUND_TYPE.color, color: '#98A2B3' },
    background: { type: BACKGROUND_TYPE.color, color: '' },
    borderColor: { type: BACKGROUND_TYPE.color, color: '' },
    iconColor: { type: BACKGROUND_TYPE.color, color: '#98A2B3' },
    hover: {
        color: { type: BACKGROUND_TYPE.color, color: '#98A2B3' },
        background: { type: BACKGROUND_TYPE.color, color: '' },
        borderColor: { type: BACKGROUND_TYPE.color, color: '' },
        iconColor: { type: BACKGROUND_TYPE.color, color: '#98A2B3' }
    },
    active: {
        color: { type: BACKGROUND_TYPE.color, color: '#101828' },
        background: { type: BACKGROUND_TYPE.color, color: '#FFFFFF' },
        borderColor: { type: BACKGROUND_TYPE.color, color: '' },
        iconColor: { type: BACKGROUND_TYPE.color, color: '#101828' }
    }
}

export const TAB_TYPE_COLOR_MAP = {
    [TABS_TYPE.CARD_SLOT]: CARD_COLOR_DEFAULT,
    [TABS_TYPE.LINE]: LINE_COLOR_DEFAULT,
    [TABS_TYPE.ROUND]: ROUND_COLOR_DEFAULT,
    [TABS_TYPE.SEGMENTED]: SEGMENTED_COLOR_DEFAULT
}

/**
 * 生成标签block的配置
 *
 * @returns {TabsBlockConfig}
 */
export const generateTabsBlock = (mainBreakPoint: string): TabsBlockConfig => {
    const length = 3
    function getListByVariantColorConfig() {
        return Array.from({ length }, (_, i) => i + 1).map(
            item =>
                ({
                    name: `标签 0${item}`,
                    id: nanoid(),
                    tabIcon: { type: 'icon', value: '' },
                    iconPosition: ALIGNMENT.left,
                    eventList: []
                    // sizeFit: SIZE_FIT.contain
                } satisfies TabConfig)
        )
    }

    const baseList = getListByVariantColorConfig()

    return {
        variant: TABS_TYPE.LINE,
        defaultTabIndex: {
            type: VariableType.VALUE,
            valueVariable: {
                type: 'number',
                value: '0'
            }
        },
        baseList,
        variantMap: {
            [TABS_TYPE.LINE]: {
                align: ALIGNMENT.center,
                styleList: baseList.map(item => ({ id: item.id, ...LINE_COLOR_DEFAULT }))
            },
            [TABS_TYPE.CARD_SLOT]: {
                align: ALIGNMENT.center,
                styleList: baseList.map(item => ({ id: item.id, ...CARD_COLOR_DEFAULT }))
            },
            [TABS_TYPE.ROUND]: {
                align: ALIGNMENT.center,
                styleList: baseList.map(item => ({ id: item.id, ...ROUND_COLOR_DEFAULT }))
            },
            [TABS_TYPE.SEGMENTED]: {
                align: ALIGNMENT.center,
                styleList: baseList.map(item => ({ id: item.id, ...SEGMENTED_COLOR_DEFAULT }))
            }
        },

        breakPoint: {
            id: mainBreakPoint,
            visibility: {
                visible: 'VISIBLE'
            },
            position: {
                type: 'relative'
            },
            size: {
                width: {
                    size: 'fill'
                },
                height: {
                    size: 'auto'
                },
                overflow: 'visible'
            },
            design: {
                lineColor: { type: BACKGROUND_TYPE.color, color: '#101828' }
            },
            font: {
                advance: '',
                align: ALIGNMENT.left,
                fontSize: {
                    size: 16,
                    unit: 'px'
                },
                lineHeight: 1,
                letterSpacing: 0
            },
            layout: {}
        },
        breakPoints: []
    }
}
